import * as React from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/styles";
import Card from "@material-ui/core/Card";
import Divider from "@material-ui/core/Divider";
import Typography from "@material-ui/core/Typography";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Alert from "@material-ui/lab/Alert";

import ImageEye from "@material-ui/icons/RemoveRedEye";
import ActionPrint from "@material-ui/icons/Print";
// import CircularProgress from "@material-ui/core/CircularProgress";
// import ActionRun from "@material-ui/icons/PlayCircleFilled";
import ActionBack from "@material-ui/icons/ArrowBack";
// import DownloadIcon from "mdi-material-ui/Download";
// import ContentCreate from "@material-ui/icons/Create";
// import InfoIcon from "@material-ui/icons/Info";
// import DeleteIcon from "@material-ui/icons/Delete";
// import AddIcon from "@material-ui/icons/Add";

// perfect scrollbar
import "react-perfect-scrollbar/dist/css/styles.css";

import { PageHeader, PageFooter, WithReduxForm, Button, translate } from "jazasoft";

import MuiTable from "../../../components/MuiTable";
import Table from "../../../components/Table";
import CardHeader from "../../../components/CardHeader";

import { useDeepMemo, useMutation, useQuery, useSetting } from "../../../hooks";
import SettingKeys from "../../../utils/SettingKeys";
import { MarkerType, OState, SpreadingType } from "../../../utils/types";
import { isEmpty, listFromCsv, findMinValue, toFixedValue, isFalse, isTrue } from "../../../utils/helpers";
import { MULTI_PART_MANUAL_PLAN, OrderFlowType, PART_SHELL } from "../../../constants";
import { printFeedback, printBundleSheet, printLayFeedback, getPlannedLayList } from "../feedbackHelper";

import useLayslip from "./useLayslip";
import LayDialog from "./LayDialog";
import IntegrationDrawer from "./IntegrationDrawer";
import { colorLightGreen, colorSecondary, colorTomatoRed } from "../../../utils/colors";
import useTenant from "../../../hooks/useTenant";
import { getGroupName, parseMarkerName, patternValue } from "../../grouping/groupingHelper";
import LiveReport from "./LiveReport";
import FPOBreakup from "./FPOBreakup";

// const NON_SB_FIELDS = ["label", "total"];

const poColumns = [
  { dataKey: "poRef", title: "PO Ref" },
  { dataKey: "customerPoRef", title: "Customer PO Ref" },
  { dataKey: "customerName", title: "Customer" },
  { dataKey: "seasonName", title: "Season" },
  { dataKey: "customField1", title: "Custom Field 1" },
  { dataKey: "customField2", title: "Custom Field 2" },
  { dataKey: "customField3", title: "Custom Field 3" },
  { dataKey: "desc", title: "Description" }
];

const StatusField = ({ record, column, onClick }) => {
  const status = record[column.dataKey];
  return (
    <div
      onClick={() => onClick && onClick(record)}
      style={{
        backgroundColor: ["Completed", "Synced"].includes(status)
          ? colorLightGreen.dark
          : ["Sent for Wash"].includes(status)
            ? colorLightGreen.light
            : ["Sync Error"].includes(status)
              ? colorTomatoRed.dark
              : colorSecondary.dark,
        color: "white",
        padding: "4px 8px",
        borderRadius: 4,
        width: "100%"
      }}>
      {status}
    </div>
  );
};

const layMapper = (lay, cutOrder, productList) => {
  const marker = cutOrder.markerList?.find(m => m.id === lay.markerId) || {};
  const status = lay.state;

  const group = cutOrder?.groupList?.find(g => marker.groupId === g.id);
  const { sizeGroup, name } = parseMarkerName(marker.name);
  const product = productList?.find(e => e.id === marker?.productId)?.name;
  return {
    ...lay,
    marker,
    group,
    layNo: lay.layNo,
    sizeGroup,
    product,
    markerName: name,
    way: marker.noOfWays,
    layLength: marker.layLength,
    groupName: !isEmpty(group) ? getGroupName(group) : `${marker.width}CM, ${patternValue(marker)}`,
    status: status
  };
};

const useStyles = makeStyles(theme => ({
  content: {
    margin: "1.5em",
    marginTop: 0
  },
  mrTop: {
    marginTop: "1.5em"
  },
  displayCenter: {
    display: "flex",
    justifyContent: "center"
  },
  margin: {
    margin: "1.5em"
  },
  form: {
    padding: "1.5em",
    paddingTop: 0
  },
  container: {
    margin: "1.5em",
    padding: 0
  },
  card: {
    margin: "1.5em 0",
    padding: 0,
    boxShadow: theme.shadows[1],
    backgroundColor: "white"
  },
  marginVertical: {
    margin: "1em 0"
  },
  table: {
    "& th, & td": {
      border: "1px solid rgba(224, 224, 224, 1)"
    },
    whiteSpace: "nowrap"
  },
  headerCells: {
    backgroundColor: "#C6D0DA"
  },
  button: {
    marginRight: "1em"
  },
  settings: {
    margin: "1em 1em 0 1em"
  },
  footer: {
    paddingBottom: "1em",
    display: "flex",
    justifyContent: "flex-start",
    "& > *": {
      marginRight: "1em"
    }
  },
  footerSetting: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "0.5rem"
  },
  drawerHeader: {
    display: "flex",
    width: 800,
    alignItems: "center",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-start"
  }
}));

const LaySlipView = props => {
  const classes = useStyles();
  const { id, roles, hasAccess, translate } = props;

  // const [editing, setEditing] = useSafeState(false);
  const [tabIdx, setTabIdx] = React.useState(0);
  const [drawerState, setDrawerState] = React.useState({ open: false, lay: null, layList: null });

  const tenant = useTenant();

  const deviationTypeStr = useSetting(SettingKeys.LAYSLIP_DEVIATION_CAUSES);
  const history = useHistory();
  const [download, { loading: downloading }] = useMutation({ rawResponse: true });

  const deviations = isEmpty(deviationTypeStr) ? [] : JSON.parse(deviationTypeStr);
  const deviationTypes = deviations.reduce((acc, el) => ({ ...acc, [el.key]: el.value }), {});

  const {
    version,
    orderFields,
    flowInfoFields,
    rollFields,
    layFields,
    messages,
    order,
    flowInfoColumns,
    flowInfoRows,
    cutOrder,
    rollList,
    layRowList,
    layList,
    layTabIdx,
    selectedKey,
    dialogState,
    mutate,
    setLayslipVersion,
    setDialogState,
    setSelectedKey,
    setLayTabIdx,
    reGenerateRemnantMarkers,
    onRefresh,
    allUnitList
  } = useLayslip(props);

  const { data: externalAppList = [] } = useQuery({ url: `externalApps`, params: { size: 1000 } });
  const preCuttingDeptName = useSetting(SettingKeys.PRE_CUTTING_DEPARTMENT_NAME);

  const layOpList = externalAppList
    ?.flatMap(externalApp => externalApp?.integrationOperationList?.map(op => ({ ...op, externalApp })) || [])
    .filter(op => op.entityId === "Lay" && op.operation === "create");
  const eventList = layOpList.map(op => (!isEmpty(op.meta) ? JSON.parse(op.meta)?.syncEvent : "")).filter(e => !isEmpty(e));
  const hasCuttingSyncEvent = eventList.some(event => event === "Cutting");

  const productIdList = cutOrder?.productIdList || [];
  const productList = productIdList.map(id => order?.flowInfoList?.find(fi => fi.product?.id === id)?.product).filter(e => !isEmpty(e));

  const poInfoColumns = poColumns.filter(c => orderFields.includes(c.dataKey));

  const poInfoRows = useDeepMemo(() => {
    if (isEmpty(order)) return [];
    return [{ ...order, customerName: order?.customer?.name, seasonName: order?.season?.name }];
  }, [order]);

  const onRowClick = row => {
    if (isEmpty(row.parentKey)) {
      setSelectedKey(row.key);
      setLayTabIdx(0);
    }
  };

  const orderConfig = cutOrder ? cutOrder.orderConfig : {};

  const fpoOrderList = useDeepMemo(() => {
    if (isEmpty(order?.orderFlowList) || isEmpty(cutOrder)) return [];
    return order.orderFlowList.filter(of => of.type === OrderFlowType.FPO && listFromCsv(of.parentIds)?.includes(`${cutOrder.id}`));
  }, [order, cutOrder]);

  const layTabs = useDeepMemo(() => {
    let layTabs = [];

    const layMarkers = layList.map(l => l.marker);
    if (layMarkers.some(m => m.markerType === MarkerType.FINAL)) {
      layTabs.push("Main Layslip");
    }
    if (layMarkers.some(m => m.markerType === MarkerType.REMNANT)) {
      layTabs.push("Remnant Layslip");
    }
    if (layMarkers.some(m => m.markerType === MarkerType.RE_CUTTING || m.markerType === MarkerType.MANUAL)) {
      layTabs.push("Manual Layslip");
    }
    return layTabs;
  }, [layList]);

  const layColumns = useDeepMemo(() => {
    let columns = [
      { dataKey: "markerNo", title: "M. No", align: "center" },
      { dataKey: "fpoMarkerNo", title: "ERP M. No", align: "center" },
      { dataKey: "product", title: "Product", align: "center" },
      { dataKey: "layNo", title: "Lay No", align: "center" },
      { dataKey: "extLayNo", title: "ERP Lay No", align: "center" },
      { dataKey: "cutNo", title: "Cut No", align: "center" },
      { dataKey: "sizeGroup", title: "Size Group", align: "center", rowSpan: ({ record }) => (record.serialNo ? record.layCount : 0) },
      { dataKey: "markerName", title: "Marker", rowSpan: ({ record }) => (record.serialNo ? record.layCount : 0) },
      { dataKey: "groupName", title: "Group", align: "center", rowSpan: ({ record }) => (record.serialNo ? record.layCount : 0) },
      { dataKey: "layLength", title: "Lay Length", align: "center", rowSpan: ({ record }) => (record.serialNo ? record.layCount : 0) },
      { dataKey: "way", title: "Way", align: "center", rowSpan: ({ record }) => (record.serialNo ? record.layCount : 0) },
      {
        dataKey: "markerPlies",
        title: "Planned Plies",
        align: "center"
        // rowSpan: ({ record }) => (record.serialNo ? record.layCount : 0)
      },
      { dataKey: "plies", title: "Planned Plies", align: "center" },
      {
        dataKey: "actualPlies",
        title: "Actual Plies",
        align: "center",
        element: ({ record, column }) => toFixedValue(record[column.dataKey], 0)
      },
      { dataKey: "rollCount", title: "No. Of Rolls", align: "center" },
      {
        dataKey: "cutQty",
        title: "Cut Qty",
        align: "center",
        element: ({ record, column }) => toFixedValue(record[column.dataKey], 0)
      },
      {
        dataKey: "fpoCutQty",
        title: "FPO Cut Qty",
        align: "center",
        element: ({ record }) => (
          <span
            style={{
              backgroundColor: record.fpoCutQty < record.cutQty ? "red" : "none",
              color: record.fpoCutQty < record.cutQty ? "white" : "none",
              padding: "3px 6px",
              borderRadius: 4
            }}>
            {record.fpoCutQty}
          </span>
        )
      },
      { dataKey: "status", title: "Status", align: "center", element: <StatusField /> },
      { dataKey: "syncStatus", title: "Sync Status", align: "center", element: <StatusField onClick={() => setDrawerState({ open: true })} /> },
      { dataKey: "action", title: "Action", align: "center" }
    ];
    if (cutOrder?.orderConfig?.multiPartBundling === MULTI_PART_MANUAL_PLAN) {
      columns = columns.filter(c => !["rollCount", "plies"].includes(c.dataKey));
    } else {
      columns = columns.filter(c => !["markerPlies"].includes(c.dataKey));
    }
    if (isEmpty(fpoOrderList) || !hasCuttingSyncEvent) {
      columns = columns.filter(c => c.dataKey !== "fpoCutQty");
    }
    if (isEmpty(layOpList) || !cutOrder?.part?.includes(PART_SHELL)) {
      columns = columns.filter(c => c.dataKey !== "syncStatus");
    }

    if (layTabIdx !== 2 || productIdList.length <= 1) {
      columns = columns.filter(c => c.dataKey !== "product");
    }

    columns = columns.filter(c => [...layFields, "product", "fpoCutQty", "action"].includes(c.dataKey));
    return columns.map(({ rowSpan, ...c }) => c);
  }, [cutOrder, layOpList, layFields, fpoOrderList, hasCuttingSyncEvent, productIdList, layTabIdx]);

  const onViewClick = lay => {
    setDialogState({ open: true, lay });
  };

  const onFeedbackPrint = () => {
    printFeedback({
      order,
      orderFlow: cutOrder,
      layList,
      layRowList,
      deviationTypes,
      flowInfoFields,
      i18nMessages: messages,
      orderFields,
      rollFields,
      tenant,
      unit: allUnitList.find(e => e.unitId === cutOrder?.unitId) || {}
    });
  };

  const onPrint = ({ layNo, type }) => {
    if (isEmpty(order) || isEmpty(cutOrder)) return;
    const options = {
      url: `bundles`,
      method: "get",
      params: { search: `orderFlow.id==${cutOrder.id}${layNo !== -1 ? `;lay.layNo==${layNo}` : ""}` }
    };
    mutate(options, {
      onSuccess: bundleSheet => {
        printBundleSheet({
          order,
          orderFlow: cutOrder,
          bundleSheet,
          type,
          tenant,
          flowInfoFields,
          orderFields: orderFields,
          rollFields,
          i18nMessages: messages
        });
        setDialogState({ open: false });
      }
    });
  };

  const downloadBundles = layNo => {
    if (isEmpty(order) || isEmpty(cutOrder)) return;
    const options = {
      url: `orders/${id}/bundles`,
      method: "get",
      headers: { Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
      params: { flowId: cutOrder.id, layNo },
      responseType: "arraybuffer"
    };

    download(options, {
      onSuccess: response => {
        const header = response.headers["content-disposition"];
        var filename = header ? header.match(/filename="(.+)"/)[1] : `BUNDLES.xlsx`;

        let blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });
        let link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;
        link.click();
      }
    });
  };

  const onLayPrint = ({ orderFlow, lay }) => {
    if (!isEmpty(order) && !isEmpty(orderFlow) && !isEmpty(lay)) {
      const finalLay = {
        ...lay,
        rowList: layRowList.filter(row => row.actualLayNo === lay.layNo)
      };
      printLayFeedback({
        order,
        orderFlow,
        lay: finalLay,
        layFields,
        tenant,
        flowInfoFields,
        orderFields,
        rollFields,
        i18nMessages: messages,
        unit: allUnitList.find(e => e.unitId === orderFlow?.unitId) || {}
      });
    }
  };

  const layRows = useDeepMemo(() => {
    const layTab = layTabs[layTabIdx];
    if (!cutOrder?.isManualPlan) {
      const plannedLayList = getPlannedLayList(cutOrder);
      const rows = layList
        .filter(lay =>
          layTab === "Main Layslip"
            ? lay.marker.markerType === MarkerType.FINAL
            : layTab === "Remnant Layslip"
              ? lay.marker.markerType === MarkerType.REMNANT
              : layTab === "Manual Layslip"
                ? lay.marker.markerType === MarkerType.RE_CUTTING
                : false
        )
        .map(lay => {
          const pLay = plannedLayList?.find(e => e.id === lay.id);
          const plies = pLay?.rowList?.reduce((acc, e) => acc + e.plannedPly, 0) || 0;
          const actualPlies = layRowList
            .filter(e => e.completed && isFalse(e.skipped) && e.actualLayNo === lay.layNo)
            .reduce((acc, row) => acc + row.actualPly || 0, 0);

          const rowList = layRowList.filter(e => e.actualLayNo === lay.layNo);
          const fpoBreakupList = !isEmpty(lay.fpoBreakup) ? JSON.parse(lay.fpoBreakup) : [];
          const fpoCutQty = fpoBreakupList.flatMap(e => e.sizeBreakupList || []).reduce((acc, sb) => acc + sb.qty, 0);

          let actionElements = [
            <ImageEye key="view" style={{ marginRight: 8, cursor: "pointer" }} onClick={() => onViewClick(lay)} />,
            <ActionPrint key="print" style={{ cursor: "pointer" }} onClick={() => onLayPrint({ orderFlow: cutOrder, lay })} />
          ];
          const externalIds = !isEmpty(lay.externalIds) ? JSON.parse(lay.externalIds) : [];

          const appIds = layOpList.map(e => e.externalApp?.appId);
          const syncedVersion = findMinValue(appIds.map(appId => externalIds.find(e => e.appId === appId)?.syncedVersion || 0));
          const syncError = appIds
            .map(appId => externalIds.find(e => e.appId === appId)?.errorMessage || "")
            .filter(e => !isEmpty(e))
            .join(", ");
          const syncStatus = syncedVersion > 0 && isEmpty(syncError) ? "Synced" : !isEmpty(syncError) ? "Sync Error" : "Not Synced";

          const marker = cutOrder.markerList?.find(m => m.id === lay.markerId) || {};
          const product = productList?.find(e => e.id === marker?.productId)?.name;

          const cutPlies = marker?.spreadingType !== SpreadingType.FACE_ONE_WAY ? actualPlies / 2 : actualPlies;

          return {
            ...lay,
            markerNo: lay.marker?.markerNo,
            fpoMarkerNo: lay.marker?.fpoMarkerNo,
            product,
            layLength: `${toFixedValue(lay.layLength)} m`,
            status: lay.state,
            syncStatus,
            plies,
            actualPlies,
            cutQty: cutPlies * (lay.marker?.noOfWays || 0),
            fpoCutQty,
            rollCount: rowList?.length,
            action: actionElements
          };
        });
      return rows;
    } else {
      const markerList = cutOrder.markerList?.sort((a, b) => a.markerNo - b.markerNo) || [];
      return markerList.flatMap(marker => {
        const mLays = layList.filter(lay => lay.markerId === marker.id).sort((a, b) => a.layNo - b.layNo);

        const plannedPlies = marker.markerPlyList.reduce((acc, mp) => acc + mp.plannedPlies, 0);

        if (!isEmpty(mLays)) {
          return mLays
            .map(lay => layMapper(lay, cutOrder, productList))
            .map(lay => {
              const rowList = layRowList?.filter(row => row.actualLayNo === lay.layNo && isTrue(row.completed) && isFalse(row.skipped)) || [];
              const actualPlies = rowList.reduce((acc, row) => acc + row.actualPly, 0);
              const difference = actualPlies - plannedPlies;
              const plannedPlyMap = marker.markerPlyList.reduce((acc, mp) => ({ ...acc, [mp.styleColor]: mp.plannedPlies }), {});
              const fpoBreakupList = !isEmpty(lay.fpoBreakup) ? JSON.parse(lay.fpoBreakup) : [];
              const fpoCutQty = fpoBreakupList.flatMap(e => e.sizeBreakupList || []).reduce((acc, sb) => acc + sb.qty, 0);

              const externalIds = !isEmpty(lay.externalIds) ? JSON.parse(lay.externalIds) : [];

              const appIds = layOpList.map(e => e.externalApp?.appId);
              const syncedVersion = findMinValue(appIds.map(appId => externalIds.find(e => e.appId === appId)?.syncedVersion || 0));
              const syncError = appIds
                .map(appId => externalIds.find(e => e.appId === appId)?.errorMessage || "")
                .filter(e => !isEmpty(e))
                .join(", ");
              const syncStatus = syncedVersion > 0 && isEmpty(syncError) ? "Synced" : !isEmpty(syncError) ? "Sync Error" : "Not Synced";

              const cutPlies = marker?.spreadingType !== SpreadingType.FACE_ONE_WAY ? actualPlies / 2 : actualPlies;

              let actionElements = [
                <ImageEye key="view" style={{ marginRight: 8, cursor: "pointer" }} onClick={() => onViewClick(lay)} />,
                <ActionPrint key="print" style={{ cursor: "pointer" }} onClick={() => onLayPrint({ orderFlow: cutOrder, lay })} />
              ];

              return {
                ...lay,
                markerNo: lay.marker?.markerNo,
                fpoMarkerNo: lay.marker?.fpoMarkerNo,
                markerPlies: plannedPlies,
                plannedPlyMap,
                actualPlies,
                cutQty: cutPlies * (lay.marker?.noOfWays || 0),
                fpoCutQty,
                difference,
                status: lay?.state,
                syncStatus,
                action: actionElements
              };
            });
        } else {
          const status = "Pending";

          const group = cutOrder?.groupList?.find(g => marker.groupId === g.id);
          const { sizeGroup, name } = parseMarkerName(marker.name);
          return [
            {
              marker,
              group,
              layNo: "",
              sizeGroup,
              markerName: name,
              way: marker.noOfWays,
              layLength: marker.layLength,
              groupName: !isEmpty(group) ? getGroupName(group) : `${marker.width}CM, ${patternValue(marker)}`,
              markerPlies: plannedPlies,
              actualPlies: 0,
              cutQty: 0,
              difference: -plannedPlies,
              status,
              syncStatus: "Not Synced",
              markerNo: marker?.markerNo
            }
          ];
        }
      });
    }
  }, [layList, layRowList, layOpList, layTabIdx, cutOrder, productList]);

  // const editableSetting = isEmpty(orderConfig?.lastBundleSerial) && (editing || isEmpty(orderConfig?.bundleSize));

  const bundleParts = listFromCsv(cutOrder?.product?.bundleParts) || [];
  let choicesParts = bundleParts.filter(e => !isEmpty(e)).map(e => ({ id: e, name: e }));

  let initialValues = {
    id: cutOrder?.id,
    bundleSize: orderConfig?.bundleSize,
    bundleTolerance: orderConfig?.bundleTolerance,
    bundleParts: orderConfig?.bundleParts?.split(",").map(e => e.trim()) || choicesParts.map(e => e.id)
  };

  // let showRePlanButton = true; // Do not show RePlan button for Replicate Cut Plan
  // if (orderConfig.multiPartBundling === MULTI_PART_EXACT_SAME_BUNDLE) {
  //   const shellOrderFlow = getOrderFlowList(order, orderFlow).find(flow => flow.part === PART_SHELL);
  //   const shellOrderConfig = shellOrderFlow ? shellOrderFlow.orderConfig : {};
  //   const shellParts = shellOrderConfig?.bundleParts?.split(",").map(e => e.trim()) || [];
  //   choicesParts = choicesParts.filter(e => !shellParts.includes(e.id));

  //   initialValues = {
  //     id: orderFlow?.id,
  //     bundleSize: shellOrderConfig?.bundleSize,
  //     bundleTolerance: shellOrderConfig?.bundleTolerance,
  //     bundleParts: orderConfig?.bundleParts?.split(",").map(e => e.trim()) || choicesParts.map(e => e.id)
  //   };
  //   showRePlanButton = false;
  // }

  return (
    <div>
      <PageHeader title="Lay Slip Feedback" />

      <IntegrationDrawer
        open={drawerState.open}
        layList={layList}
        order={order}
        layOpList={layOpList}
        orderFlow={cutOrder}
        preCuttingDeptName={preCuttingDeptName}
        roles={roles}
        hasAccess={hasAccess}
        onRefresh={onRefresh}
        onClose={() => setDrawerState({ open: false })}
      />

      <LayDialog
        open={dialogState?.open || false}
        downloading={downloading}
        order={order}
        orderFlow={cutOrder}
        rollFields={rollFields}
        lay={dialogState.lay}
        missingRolls={dialogState.missingRolls}
        deviationTypes={deviationTypes}
        layRowList={layRowList}
        onClose={() => setDialogState({ open: false })}
        onRefresh={() => setLayslipVersion(v => v + 1)}
        reGenerateRemnantMarkers={reGenerateRemnantMarkers}
        onPrint={onPrint}
        downloadBundles={downloadBundles}
      />

      <div className={classes.content}>
        {/* PO Details */}
        <MuiTable title="PO Details" toolbar columns={poInfoColumns} rows={poInfoRows} i18nKey="resources.orders.fields" />

        <div className={classes.mrTop}>
          <MuiTable
            key={version}
            title={translate("resources.layslip.title.cut_order", { _: "Cut Order" })}
            toolbar={true}
            searchable={true}
            pageable={true}
            searchKeys={["style", "color", "flowRef", "customerFlowRef", "fpo"]}
            idKey="key"
            parentIdKey="parentKey"
            columns={flowInfoColumns}
            rows={flowInfoRows}
            defaultExpanded={false}
            expandedColor={["#BFDDF8", "#7AB9F0"]}
            onRowClick={onRowClick}
            rowStyle={({ row }) => ({ backgroundColor: selectedKey === row.key ? "#BFDDF8" : "none" })}
            i18nKey="resources.grouping.fields.flowInfo"
          />
        </div>

        {cutOrder?.orderConfig?.multiPartBundling !== MULTI_PART_MANUAL_PLAN && isEmpty(cutOrder?.orderConfig?.layType) && (
          <Alert variant="filled" severity="warning" className={classes.mrTop}>
            <Typography variant="h6">
              {cutOrder?.stateValue < OState.MAIN_CUTPLAN_COMPLETED.stateValue ? "Planning not done yet" : "Roll Plan not generated yet."}{" "}
            </Typography>
          </Alert>
        )}

        {cutOrder && (cutOrder.reGenerated || cutOrder.stateValue >= OState.MAIN_CUTPLAN_COMPLETED.stateValue) && (
          <WithReduxForm key={version} initialValues={initialValues}>
            {({ handleSubmit, ...rest }) => (
              <div>
                {/* Live Report */}
                <Card className={classes.mrTop}>
                  {!isEmpty(fpoOrderList) && hasCuttingSyncEvent ? (
                    <Tabs value={tabIdx} onChange={(e, tabIdx) => setTabIdx(tabIdx)}>
                      <Tab label="Live Report" />
                      <Tab label="FPO Breakup" />
                    </Tabs>
                  ) : (
                    <CardHeader title="Live Report" />
                  )}
                  <Divider />

                  {tabIdx === 0 && <LiveReport order={order} cutOrder={cutOrder} rollList={rollList} layRowList={layRowList} />}
                  {tabIdx === 1 && <FPOBreakup order={order} cutOrder={cutOrder} fpoOrderList={fpoOrderList} onRefresh={onRefresh} />}
                </Card>

                <Card className={classes.mrTop}>
                  {/*Tabs for Lay Type */}
                  <Tabs value={layTabIdx} onChange={(e, idx) => setLayTabIdx(idx)} variant="scrollable" scrollButtons="auto">
                    {layTabs.map((tab, idx) => (
                      <Tab key={idx} label={tab === "All Mix" ? "Main Lay" : tab} />
                    ))}
                  </Tabs>
                  <Divider />

                  <div>
                    <Table columns={layColumns} rows={layRows} i18nKey="resources.layslip.fields.layslip" />
                  </div>
                </Card>
              </div>
            )}
          </WithReduxForm>
        )}

        <PageFooter className={classes.footer}>
          {/* {cutOrder?.stateValue === OState.MAIN_FEEDBACK_COMPLETED.stateValue &&
            showRePlanButton &&
            hasPrivilege(roles, hasAccess, ResourceId.CUTPLAN, Scope.WRITE) && (
              <Button
                className={classes.button}
                label="Replan"
                variant="contained"
                color={rePlanning ? "default" : "primary"}
                onClick={onReGenerateClick}
                disabled={rePlanning}>
                {rePlanning ? <CircularProgress size={20} thickness={2} color="secondary" /> : <ActionRun />}
              </Button>
            )} */}
          {cutOrder?.stateValue >= OState.MAIN_FEEDBACK_IN_PROGRESS.stateValue && (
            <>
              <Button label="Lay Slip" variant="contained" color="primary" onClick={onFeedbackPrint}>
                <ActionPrint />
              </Button>
              {/* <SplitButton
                variant="outlined"
                primaryIcon={<ActionPrint />}
                primaryLabel="Bundle Plan"
                options={Object.values(BundleSheetType)}
                disabled={busy}
                onClick={({ type }) => onPrint({ layNo: -1, type })}
              />
              <Button label="Download Bundle Plan" variant="outlined" color="primary" disabled={downloading} onClick={() => downloadBundles(-1)}>
                <DownloadIcon />
              </Button> */}
            </>
          )}
          <Button label="Back" variant="outlined" color="primary" onClick={() => history.push("/feedback/layslip")}>
            <ActionBack />
          </Button>
        </PageFooter>
      </div>
    </div>
  );
};

export default translate(LaySlipView);
